/**
 * Fila Virtual — 줄서기 고객 화면 (`/fila/:storeSlug?p={qrSlug}`).
 *
 * QR 로 진입해 (선택→)등록 → 대기 → 호출 → 착석/수령/이탈까지 상태에 따라 전환한다.
 * 테이블(좌석 대기)과 픽업(카운터 주문 대기) 2종 큐를 지원한다(#794).
 *
 * @see #781 (menu PR3) / admin#2111
 * @see olulo-mx-admin/docs/plans/2026-07-16-waitlist-design.md §4 · §6.1 · §7 · §8
 * @see olulo-mx-admin/.superpowers/sdd/task-10-brief.md
 *
 * ## 화면 결정 순서 (토큰 보유자 우선)
 * 1. 매장/설정 로딩 → 로딩 표시
 * 2. QR 비활성 · 매장 조회 실패 → 접수 불가 화면 (설계 §4)
 * 3. 보관 토큰 + 파티 있음 → `party.queue_type` 로 분기:
 *    - `pickup`: waiting/called/preparing/ready/seated(→Delivered)/cancelled 체인
 *    - 그 외(테이블·구서버): 기존 waiting/called/seated/cancelled 체인
 * 4. 토큰만 있고 파티 미도착 → 로딩(깜빡임 방지)
 * 5. 토큰 없음 → 큐 게이트:
 *    - `queues` 있음 + 둘 다 활성 → 선택 화면(mode 미정 시) → 해당 큐 등록 화면
 *    - `queues` 있음 + 한쪽만 활성 → 그 큐 등록 화면 자동
 *    - `queues` 있음 + 둘 다 비활성 → 접수 불가
 *    - `queues` 없음(구서버) → 기존 단일(top-level enabled) 큐 동작으로 폴백
 *
 * ## 접수 중지와 이미 대기 중인 팀 (설계 §8-1)
 * `enabled=false` 는 **신규 등록만** 차단한다. 토큰 보유자는 그대로 대기 화면을 본다 —
 * 토큰 분기를 큐 게이트보다 먼저 두는 이유다.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLocale } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import { ApiClientError, storeApi } from '@/lib/api';
import {
  isWaitlistPauseReason,
  isWaitlistQueueAvailable,
  isWaitlistQueueOpen,
  toApiLocale,
  waitlistApi,
  type WaitlistPauseReason,
  type WaitlistPartyData,
  type WaitlistQueueType,
  type WaitlistStatusData,
} from '@/lib/api/waitlist';
import type { StoreDetail } from '@/lib/api/types';
import { useWaitlistParty } from '@/hooks/waitlist/useWaitlistParty';
import { useMarketingConsentWithdrawal } from '@/hooks/waitlist/useMarketingConsentWithdrawal';
import { useStoreMarketingFollow } from '@/hooks/waitlist/useStoreMarketingFollow';
import {
  WaitlistCancelledScreen,
  WaitlistChooseScreen,
  WaitlistDeliveredScreen,
  WaitlistFormScreen,
  WaitlistMarketingConsentWithdraw,
  WaitlistPickupReadyScreen,
  WaitlistPrepScreen,
  WaitlistReadyScreen,
  WaitlistSeatedScreen,
  WaitlistStoreFollowToggle,
  WaitlistUnavailableScreen,
  WaitlistWaitingScreen,
  clearWaitlistMarketingConsent,
  clearWaitlistMode,
  clearWaitlistToken,
  readWaitlistMode,
  readWaitlistToken,
  saveWaitlistMarketingConsent,
  saveWaitlistMode,
  saveWaitlistToken,
  WAITLIST_STRINGS,
  type WaitlistArriveError,
  type WaitlistJoinInput,
  type WaitlistUnavailableReason,
} from '@/components/waitlist';
import { buildWaitlistJoinPayload, maskWaitlistContact } from '@/components/waitlist/utils';
import { SUPPORTED_LOCALES } from '@/config/locales';
import { logger } from '@/utils/logger';
import { safeErrorMetadata } from '@/utils/safeErrorMetadata';
import { requestCurrentCoordinates } from '@/lib/geolocation';

/** ApiClientError 의 error_code 추출 (없으면 null). */
function errorCodeOf(error: unknown): string | null {
  return error instanceof ApiClientError ? (error.code ?? null) : null;
}

/** QUEUE_PAUSED 응답에서 고객 공개 enum 만 추출한다. 자유 입력 메모는 읽지 않는다. */
function pauseReasonOf(error: unknown): WaitlistPauseReason | null {
  if (!(error instanceof ApiClientError) || typeof error.body !== 'object' || error.body === null) {
    return null;
  }

  const reason = (error.body as { pause_reason?: unknown }).pause_reason;
  return isWaitlistPauseReason(reason) ? reason : null;
}

export function FilaVirtualPage() {
  const { storeSlug = '' } = useParams<{ storeSlug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { locale, setLocale } = useLocale();
  const localeRef = useRef(locale);
  useLayoutEffect(() => { localeRef.current = locale; }, [locale]);

  const qrSlug = searchParams.get('p') ?? undefined;
  // WhatsApp 알림 버튼 딥링크(?t=access_token) — 알림을 여는 브라우저에는 등록 시
  // 저장한 localStorage 토큰이 없으므로 URL 토큰을 우선 채택한다 (#787 / admin#2131).
  const urlToken = searchParams.get('t');
  const urlTokenRef = useRef(urlToken);
  useLayoutEffect(() => { urlTokenRef.current = urlToken; }, [urlToken]);
  const t = WAITLIST_STRINGS[locale] ?? WAITLIST_STRINGS.es;
  const direction = SUPPORTED_LOCALES.find((l) => l.code === locale)?.direction ?? 'ltr';

  const [store, setStore] = useState<StoreDetail | null>(null);
  const [status, setStatus] = useState<WaitlistStatusData | null>(null);
  const [statusLocale, setStatusLocale] = useState<string | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [unavailable, setUnavailable] = useState<WaitlistUnavailableReason | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  // 큐 선택 화면(ChooseScreen) 복귀용 모드 — 양쪽 큐가 모두 열린 매장에서만 의미가 있다
  // (설계 §8-1 / #794 확장). storage 복원은 bootstrap 성공 시 1회 수행한다.
  const [mode, setMode] = useState<WaitlistQueueType | null>(null);

  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [startPosition, setStartPosition] = useState(0);

  /**
   * 등록 직후 대기 화면에 띄울 연락처 폴백 — 입력값을 마스킹한 표시용 문자열(admin#2504).
   *
   * 번호 금고 전환 뒤 서버는 원문을 응답에 싣지 않고 마스킹 값(`contact_display`)만
   * 준다. 그 값이 도착하기 전(등록 → 첫 상태 응답 사이)에는 화면에 표시할 연락처가
   * 아예 없어 "어디로 알림이 가는지"가 잠깐 사라진다 — 그 창을 이 값으로 메운다.
   *
   * **메모리에만 둔다.** localStorage 로 옮기면 번호에서 파생된 값을 기기에 새로
   * 영속화하는 것이라 이번 변경이 없애려는 노출면을 되살린다. 새로고침하면 사라지고
   * 서버 값만 남는 것이 의도된 동작이다.
   */
  const [contactFallback, setContactFallback] = useState<string | null>(null);

  const [arriving, setArriving] = useState(false);
  const [arriveError, setArriveError] = useState<WaitlistArriveError | null>(null);
  const [leaving, setLeaving] = useState(false);

  const actionScope = useMemo(
    () => ({ accessToken, storeId: store?.id ?? null, storeSlug, qrSlug, urlToken }),
    [accessToken, store?.id, storeSlug, qrSlug, urlToken],
  );
  const activeActionScopeRef = useRef<typeof actionScope | null>(null);
  const pendingJoinRef = useRef<typeof actionScope | null>(null);
  // A state update cannot block two callbacks in the same render. Keep one
  // arrival/leave write pending per committed party, including the GPS await.
  const pendingPartyActionRef = useRef<typeof actionScope | null>(null);
  useLayoutEffect(() => {
    activeActionScopeRef.current = actionScope;
    setArriving(false);
    setArriveError(null);
    setLeaving(false);
    setJoining(false);
    setJoinError(null);
    return () => { activeActionScopeRef.current = null; };
  }, [actionScope]);

  const routeScope = useMemo(() => ({ storeSlug, qrSlug }), [storeSlug, qrSlug]);
  const activeRouteRef = useRef<typeof routeScope | null>(null);
  useLayoutEffect(() => {
    activeRouteRef.current = routeScope;
    return () => { activeRouteRef.current = null; };
  }, [routeScope]);

  const intakeScope = useMemo(() => ({ routeScope, storeId: store?.id, locale }), [routeScope, store?.id, locale]);
  const activeIntakeRef = useRef<typeof intakeScope | null>(null);
  const intakeRequestRef = useRef(0);
  useLayoutEffect(() => {
    activeIntakeRef.current = intakeScope;
    return () => { activeIntakeRef.current = null; };
  }, [intakeScope]);

  const { party, loading: partyLoading, error: partyError, stale: partyStale, refreshing: partyRefreshing, notFound, setParty, refresh: refreshParty } = useWaitlistParty(accessToken);

  /** 토큰이 살아 있는 상태 화면인가 — 마케팅 통제 조각(철회·팔로우)의 공통 전제. */
  const partyScreenActive = Boolean(!bootstrapping && !unavailable && accessToken && party);

  /**
   * 마케팅 동의 셀프 철회(admin#2472) — 토큰이 살아 있는 상태 화면 전체의 공통 푸터.
   *
   * 대기중/호출됨/착석/픽업(준비중·수령준비·수령완료) 어느 화면이든 철회 대상은 같은
   * 연락처이므로, 화면별로 붙이지 않고 페이지 하단에 한 번만 둔다.
   *
   * 취소(cancelled) 화면도 제외하지 않는다 — 줄 이탈은 마케팅의 **끝이 아니라 시작**
   * 이다(이탈 고객 초대장 발송 대상). 이탈 직후에 철회 경로가 사라지면 #884 가 없애려던
   * "수신 대상인데 링크를 못 봄"이 그대로 재생산된다. 이탈 시 `clearWaitlistToken` 이
   * 로컬 플래그를 지우므로 구서버에서는 어차피 링크가 없고, 신서버에서는 서버의
   * `marketing_consent.current` 가 노출 여부를 판정한다. 토큰은 메모리에 남아 있어
   * 철회 호출 자체도 가능하다.
   */
  const consentWithdrawal = useMarketingConsentWithdrawal(
    store?.id ?? null,
    accessToken,
    partyScreenActive,
    // 서버가 알려주는 "이 연락처가 지금 수신 대상인가"(admin#2493). 구서버는 undefined —
    // 그때만 매장별 localStorage 플래그로 폴백한다(#884). 필드가 늘어도(store_followed)
    // 훅이 보는 것은 current 하나이므로 객체를 그대로 넘긴다.
    party?.marketing_consent,
  );

  /**
   * 매장 팔로우 토글(admin#2496 / #885) — 철회와 **다른 축**이라 훅도 따로 둔다.
   *
   * 철회는 연락처 전역·비가역이고 팔로우는 이 매장 하나·가역이다. 화면에서도 가벼운
   * 선택(토글)을 먼저 보여주고 무거운 선택(전역 철회)을 그 아래 둔다 — 전역 철회를
   * 누르기 전에 "이 매장만 끄면 된다"는 선택지를 먼저 보게 하는 것이 고객 이익에 맞다
   * (follow 설계 §4.1).
   */
  /**
   * 팔로우 토글이 끝나면(성공·실패 무관) 상태를 한 번 다시 읽는다.
   *
   * 훅의 낙관 덮개는 "settle 이후 처음 도착하는 상태 응답"에서만 걷히는데, 착석·취소
   * 화면은 폴링이 멈춰 있어(useWaitlistParty 의 종결 판정) 그 응답이 영영 오지 않는다 —
   * 커밋 뒤 응답이 유실되면 화면은 롤백값과 오류 문구를 단 채 서버의 반대 값과 영구히
   * 어긋난다(#885). 전역 철회 직후에 쓰는 처치와 같은 한 번의 재조회로 닫는다.
   */
  const handleStoreFollowSettled = useCallback(() => {
    void refreshParty();
  }, [refreshParty]);

  const storeFollow = useStoreMarketingFollow(
    accessToken,
    partyScreenActive,
    party?.marketing_consent,
    handleStoreFollowSettled,
  );

  /**
   * 전역 철회를 마친 뒤에는 팔로우 토글을 렌더하지 않는다.
   *
   * 철회는 연락처 전체의 수신을 끊으므로 그 뒤의 매장 팔로우는 의미가 없는데, 훅이 보는
   * 서버 `current` 는 전파 지연으로 잠시(종결 화면에서는 폴링이 멈춰 **영영**) true 로
   * 남는다. 그대로 두면 같은 화면에 "이제 이 번호로는 프로모션을 받지 않습니다"와 "이
   * 번호로 이 매장의 쿠폰·소식을 받습니다"(aria-checked=true)가 동시에 선다 — 고객이
   * 무엇을 믿어야 할지 알 수 없다. 철회 완료는 토큰 단위로 기억되므로(새로고침 포함)
   * 이 판정도 그 기억을 따른다.
   */
  const storeFollowVisible = storeFollow.visible && !consentWithdrawal.withdrawn;

  /**
   * 철회에 성공하면 상태를 한 번 다시 읽는다.
   *
   * 폴링(15초)을 기다리는 동안 `marketing_consent` 가 옛 값으로 남아 화면이 모순된
   * 조각을 띄우는 창을 없앤다. 착석·취소 화면은 폴링이 아예 멈춰 있어(useWaitlistParty
   * 의 종결 판정) 이 한 번이 **유일한** 갱신 기회다. 토큰당 한 번만 부른다 — 완료
   * 상태는 계속 참으로 남으므로 조건만 보면 매 렌더 재호출된다.
   */
  const withdrawnRefreshedTokenRef = useRef<string | null>(null);
  useEffect(() => {
    if (!consentWithdrawal.withdrawn || !accessToken) {
      return;
    }
    if (withdrawnRefreshedTokenRef.current === accessToken) {
      return;
    }
    withdrawnRefreshedTokenRef.current = accessToken;
    void refreshParty();
  }, [consentWithdrawal.withdrawn, accessToken, refreshParty]);

  // URL 토큰 검증 성공(파티 조회됨) 시에만 영속화 (#789).
  useEffect(() => {
    if (!store || !urlToken || accessToken !== urlToken || !party) {
      return;
    }
    saveWaitlistToken(store.id, urlToken);
  }, [party, store, urlToken, accessToken]);

  // 같은 라우트에서 ?t= 값만 바뀌는 경우(예: WhatsApp 알림을 다시 열었을 때 새 토큰으로
  // 링크가 갱신됨) bootstrap effect 는 storeSlug/qrSlug/reloadKey 에만 의존해 재실행되지
  // 않는다 — urlToken 이 실제로 바뀔 때만 재검증(채택)한다(#794 리뷰).
  //
  // `accessToken` 을 조건에 넣지 않는 이유: 아래 404 정리 effect 는 무효 urlToken 을
  // 폐기하고 저장 토큰으로 되돌릴 수 있는데(accessToken !== urlToken 이 되는 정상
  // 결과), 이 effect 가 그 상태를 accessToken 기준으로 감시하면 즉시 urlToken 으로
  // 되돌려버려 두 effect 가 무한히 핑퐁한다. 대신 "이 urlToken 값을 이미 한 번
  // 채택 시도했는지"만 ref 로 추적해, urlToken 자체가 새 값으로 바뀔 때만 반응한다.
  const lastAdoptedUrlTokenRef = useRef<string | null>(null);
  useEffect(() => {
    if (!store || !urlToken || lastAdoptedUrlTokenRef.current === urlToken) {
      return;
    }
    lastAdoptedUrlTokenRef.current = urlToken;
    setAccessToken(urlToken);
  }, [store, urlToken]);

  /**
   * 매장 해석 + 접수 현황 조회.
   *
   * 등록 라우트가 숫자 storeId 바인딩(`whereNumber`)이라 슬러그를 먼저 id 로 바꿔야 한다.
   * `reloadKey` 증가 시 재실행 — 일시적 실패의 재시도 경로.
   */
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!storeSlug) {
      setUnavailable('error');
      setBootstrapping(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    const active = () => !cancelled && activeRouteRef.current === routeScope;

    async function bootstrap() {
      setBootstrapping(true);
      setUnavailable(null);
      let operation: 'store-read' | 'storage-restore' | 'status-read' | 'qr-status-read' = 'store-read';

      try {
        const storeResponse = await storeApi.getStore(storeSlug, controller.signal);
        if (!active()) {
          return;
        }
        const resolved = storeResponse.data;
        setStore(resolved);
        operation = 'storage-restore';
        // URL 토큰은 검증(파티 조회 성공) 전에는 저장하지 않는다 — 악의적 링크의
        // 무효 토큰이 저장된 유효 토큰을 덮어쓰는 것을 막는다 (#789 보안 리뷰).
        setAccessToken(urlTokenRef.current ?? readWaitlistToken(resolved.id));
        // 큐 선택 복원 — 둘 다 열린 매장에서 이전에 고른 모드를 기억한다(#794).
        setMode(readWaitlistMode(resolved.id));

        // 유효 QR 이면 서버가 스캔 카운트를 올린다 — 진입당 1회만 호출해야 한다
        // (설계 §4). reloadKey 재시도는 실패 후에만 발생하므로 중복 집계가 아니다.
        const requestedLocale = toApiLocale(localeRef.current);
        operation = qrSlug ? 'qr-status-read' : 'status-read';
        const statusResponse = await waitlistApi.getStatus(
          resolved.id,
          requestedLocale,
          qrSlug,
          controller.signal,
        );
        if (!active()) {
          return;
        }
        setStatus(statusResponse.data);
        setStatusLocale(requestedLocale);
      } catch (error) {
        if (!active() || (error instanceof DOMException && error.name === 'AbortError')) {
          return;
        }

        const code = errorCodeOf(error);
        if (code === 'QR_INACTIVE') {
          setUnavailable('qr_inactive');
        } else {
          // 매장 미존재(404/STORE_NOT_FOUND)와 네트워크 실패를 구분해 재시도를 제안한다.
          setUnavailable('error');
          logger.warn('[waitlist] 진입 초기화 실패', { error: safeErrorMetadata(error) ?? { type: 'Unknown' }, diagnostic_marker: 'menu.waitlist.bootstrap.v1', operation });
        }
      } finally {
        if (active()) {
          setBootstrapping(false);
        }
      }
    }

    void bootstrap();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [storeSlug, qrSlug, reloadKey, routeScope]);

  /** QR 재집계 없이 신규 접수 게이트만 갱신한다. 실패해도 현재 화면은 유지한다. */
  const refreshIntakeStatus = useCallback(
    async (signal?: AbortSignal) => {
      if (!store || activeIntakeRef.current !== intakeScope || signal?.aborted) {
        return;
      }
      const request = ++intakeRequestRef.current;
      const active = () => activeIntakeRef.current === intakeScope && intakeRequestRef.current === request && !signal?.aborted;
      try {
        const requestedLocale = toApiLocale(locale);
        const response = await waitlistApi.getStatus(store.id, requestedLocale, undefined, signal);
        if (!active()) return;
        setStatus(response.data);
        setStatusLocale(requestedLocale);
      } catch (error) {
        if (active() && !(error instanceof DOMException && error.name === 'AbortError')) {
          logger.warn('[waitlist] 신규 접수 상태 갱신 실패', { error: safeErrorMetadata(error) ?? { type: 'Unknown' }, diagnostic_marker: 'menu.waitlist.intake.v1', operation: 'status-read' });
        }
      }
    },
    [store, locale, intakeScope],
  );

  /**
   * 언어 전환 시 QR 스캔을 재집계하지 않고 해당 locale의 서버 소유 동의 문구만
   * 포함한 최신 status를 다시 받는다. 응답 전에는 statusLocale 불일치로 동의 UI를
   * 숨겨 오래된 문구/증거와 새 등록 locale이 섞이지 않게 한다.
   */
  useEffect(() => {
    if (!store || bootstrapping || !status) {
      return;
    }

    const requestedLocale = toApiLocale(locale);
    if (statusLocale === requestedLocale) {
      return;
    }

    const controller = new AbortController();
    void refreshIntakeStatus(controller.signal);
    return () => controller.abort();
  }, [store, bootstrapping, status, statusLocale, locale, refreshIntakeStatus]);

  /**
   * 토큰이 없는 신규 접수 화면만 15초마다 큐 게이트를 갱신한다(#809).
   *
   * 최초 진입과 달리 `p`를 보내지 않아 QR 스캔 카운트를 중복 증가시키지 않는다.
   * 이미 대기번호를 받은 고객은 useWaitlistParty 폴링이 우선이며 이 폴링은 중단된다.
   */
  useEffect(() => {
    if (!store || bootstrapping || unavailable || accessToken) {
      return;
    }

    const controller = new AbortController();
    let refreshing = false;

    const intervalId = window.setInterval(() => {
      if (refreshing) {
        return;
      }
      refreshing = true;
      void refreshIntakeStatus(controller.signal).finally(() => {
        refreshing = false;
      });
    }, 15_000);

    return () => {
      controller.abort();
      window.clearInterval(intervalId);
    };
  }, [store, bootstrapping, unavailable, accessToken, refreshIntakeStatus]);

  // 서버에 없는 토큰(404) 정리 — 무효 ?t= 복귀와의 경합 해소(#794 리뷰).
  //
  // 이전에는 이 effect 와 위 "URL 토큰 검증" effect 가 별도로 존재해, 실패한 토큰이
  // urlToken 인 경우 위 effect 가 저장 토큰으로 복귀(setAccessToken)를 시도해도 같은
  // 커밋에서 이 effect 가 여전히 notFound=true 를 보고 그 저장 토큰까지 지워버렸다.
  // 하나의 effect 로 합쳐, 실패한 토큰이 URL 토큰이고 별도의 저장 토큰이 남아있으면
  // 그 토큰은 지우지 않고 복귀만 하며, 그 외(저장 토큰 자체가 만료/삭제됨)에만 폐기한다.
  useEffect(() => {
    if (!notFound || !store) {
      return;
    }

    const storedToken = readWaitlistToken(store.id);

    if (urlToken && accessToken === urlToken && storedToken && storedToken !== urlToken) {
      setAccessToken(storedToken);
      return;
    }

    clearWaitlistToken(store.id);
    setAccessToken(null);
    setContactFallback(null);
    // 등록 화면으로 되돌아가므로 큐 게이트(활성 여부·대기수)를 최신으로 다시 조회한다 —
    // 대기 중 매장 설정(접수 중지/재개, 큐별 활성화)이 바뀌었을 수 있다(#794 리뷰).
    setReloadKey((key) => key + 1);
  }, [notFound, store, urlToken, accessToken]);

  const handleJoin = useCallback(
    async (input: WaitlistJoinInput) => {
      if (!store || activeActionScopeRef.current !== actionScope || pendingJoinRef.current === actionScope) {
        return;
      }

      // 테이블 모드에 partySize 가 없으면 제출 자체를 막는다 — 이전에는
      // `input.partySize ?? 1` 로 침묵 fallback 해 사용자가 모르는 사이 1인석으로
      // 등록될 수 있었다(#794 Task 9 코드리뷰 시정). 가드/이유는 buildWaitlistJoinPayload 참고.
      const payload = buildWaitlistJoinPayload(input);
      if (!payload) {
        setJoinError(t.errorGenerico);
        logger.warn('[waitlist] 테이블 모드에 partySize 가 없어 등록 제출을 차단함');
        return;
      }

      pendingJoinRef.current = actionScope;
      setJoining(true);
      setJoinError(null);
      let operation: 'join-write' | 'token-persist' | 'consent-persist' = 'join-write';

      try {
        const response = await waitlistApi.join(
          store.id,
          {
            ...payload,
            phone: input.phoneDigits,
            dial_code: input.dialCode,
            locale: toApiLocale(locale),
          },
          qrSlug,
        );

        if (activeActionScopeRef.current !== actionScope) return;
        operation = 'token-persist';
        saveWaitlistToken(store.id, response.data.access_token);
        operation = 'consent-persist';

        // 마케팅 동의 셀프 철회 링크 노출 판정의 근거(admin#2472). 서버가 실제로
        // 증거를 기록한 `recorded` 일 때만 플래그를 남긴다 — 그 외(미요청/기록 실패/
        // 구서버로 필드 부재)는 철회할 대상이 없으므로 남은 플래그까지 정리한다.
        const consentStatus = response.data.marketing_consent?.status;
        if (consentStatus === 'recorded') {
          saveWaitlistMarketingConsent(store.id);
        } else {
          if (payload.marketing_consent && consentStatus === 'not_recorded') {
            // 사용자는 체크했는데 서버가 기록하지 않은 경우(문구 revision 불일치 등).
            // 등록 자체는 성공이라 동선을 막지 않고, 운영 추적용 경고만 남긴다.
            // 전화번호·동의 문구 등 개인정보는 로그에 담지 않는다.
            logger.warn('[waitlist] 마케팅 동의를 선택했으나 서버가 not_recorded 로 응답함');
          }
          clearWaitlistMarketingConsent(store.id);
        }

        // 원문이 아니라 마스킹한 표시값만 메모리에 남긴다(admin#2504).
        setContactFallback(maskWaitlistContact(input.dialCode, input.phoneDigits));
        setStartPosition(response.data.position);
        setAccessToken(response.data.access_token);
      } catch (error) {
        if (activeActionScopeRef.current !== actionScope) return;
        const code = errorCodeOf(error);
        if (code === 'DUPLICATE_PARTY') {
          setJoinError(t.duplicado);
        } else if (code === 'WAITLIST_CLOSED') {
          // 화면을 띄운 사이 매장이 접수를 닫았다 — 등록 화면을 유지하지 않고 안내로 전환.
          setUnavailable('closed');
        } else if (code === 'PARTY_SIZE_TOO_LARGE') {
          setJoinError(t.warnMax(status?.max_party_size ?? input.partySize ?? 1));
        } else if (code === 'QR_INACTIVE') {
          setUnavailable('qr_inactive');
        } else if (code === 'QUEUE_PAUSED') {
          // 화면 표시 후 운영자가 해당 큐를 멈춘 경합. 응답의 안전한 enum 사유만
          // 즉시 반영하고, 전체 현황도 다시 조회해 다른 큐 선택 가능 여부를 동기화한다.
          const pausedMode = payload.queue_type;
          const pauseReason = pauseReasonOf(error);
          setStatus((current) => {
            if (!current?.queues) {
              return current;
            }

            const queue = current.queues[pausedMode];
            return {
              ...current,
              queues: {
                ...current.queues,
                [pausedMode]: {
                  ...queue,
                  enabled: false,
                  intake_status: 'paused' as const,
                  pause_reason: pauseReason,
                },
              },
            };
          });
          setJoinError(null);
          void refreshIntakeStatus();
        } else if (code === 'QUEUE_INACTIVE') {
          // 화면을 띄운 사이 선택한 큐(테이블/픽업)만 비활성화됐다 — 현황을 다시 불러와
          // 다른 큐로 자동 전환되거나(한쪽만 활성) 접수 불가로 안내한다(#794).
          setJoinError(t.errorGenerico);
          void refreshIntakeStatus();
        } else {
          setJoinError(t.errorGenerico);
          logger.warn('[waitlist] 등록 실패', { error: safeErrorMetadata(error) ?? { type: 'Unknown' }, diagnostic_marker: 'menu.waitlist.join.v1', operation });
        }
      } finally {
        if (pendingJoinRef.current === actionScope) pendingJoinRef.current = null;
        if (activeActionScopeRef.current === actionScope) setJoining(false);
      }
    },
    [store, locale, qrSlug, t, status, refreshIntakeStatus, actionScope],
  );

  const handleArrive = useCallback(async () => {
    if (!accessToken || activeActionScopeRef.current !== actionScope || pendingPartyActionRef.current === actionScope) {
      return;
    }

    pendingPartyActionRef.current = actionScope;
    setArriving(true);
    setArriveError(null);
    let operation: 'geolocation' | 'arrive-write' = 'geolocation';

    try {
      // 매장이 GPS 검증을 쓰지 않으면 좌표를 요구하지 않는다 — 불필요한 권한 프롬프트 차단.
      const coords = status?.gps_verification ? await requestCurrentCoordinates() : null;
      if (activeActionScopeRef.current !== actionScope) return;
      operation = 'arrive-write';
      const response = await waitlistApi.arrive(
        accessToken,
        coords ? { lat: coords.lat, lng: coords.lng } : {},
      );

      if (activeActionScopeRef.current !== actionScope) return;

      // 도착 확인은 파트너의 착석 처리를 기다리는 단계다 — 상태는 그대로 called 일 수 있다.
      // 폴링 주기(15s)를 기다리지 않고 서버가 준 status 를 즉시 반영한다.
      setParty({
        ...(party as WaitlistPartyData),
        status: response.data.status,
        gps_verified_at: response.data.gps_verified_at,
        // 구서버 호환: 응답에 없으면 기존 값을 유지한다(#794 확장 필드).
        arrived_at: response.data.arrived_at ?? party?.arrived_at ?? null,
      });
    } catch (error) {
      if (activeActionScopeRef.current !== actionScope) return;
      const code = errorCodeOf(error);
      if (code === 'TOO_FAR') {
        setArriveError('TOO_FAR');
      } else if (code === 'GPS_UNAVAILABLE') {
        setArriveError('GPS_UNAVAILABLE');
      } else {
        setArriveError('GENERIC');
        logger.warn('[waitlist] 도착 확인 실패', { error: safeErrorMetadata(error) ?? { type: 'Unknown' }, diagnostic_marker: 'menu.waitlist.arrive.v1', operation });
      }
    } finally {
      if (pendingPartyActionRef.current === actionScope) pendingPartyActionRef.current = null;
      if (activeActionScopeRef.current === actionScope) setArriving(false);
    }
  }, [accessToken, status, party, setParty, actionScope]);

  const handleLeave = useCallback(async () => {
    if (!accessToken || !store || activeActionScopeRef.current !== actionScope || pendingPartyActionRef.current === actionScope) {
      return;
    }

    pendingPartyActionRef.current = actionScope;
    setLeaving(true);
    let operation: 'leave-write' | 'storage-clear' = 'leave-write';

    try {
      const response = await waitlistApi.leave(accessToken);
      if (activeActionScopeRef.current !== actionScope) return;
      setParty({
        ...(party as WaitlistPartyData),
        status: response.data.status,
        cancel_reason: response.data.cancel_reason,
      });
      // 종결 토큰을 남기면 재방문 시 항상 취소 화면이 떠 재등록 동선이 막힌다.
      operation = 'storage-clear';
      const savedToken = readWaitlistToken(store.id);
      if (!savedToken || savedToken === accessToken) clearWaitlistToken(store.id);
    } catch (error) {
      if (activeActionScopeRef.current !== actionScope) return;
      logger.warn('[waitlist] 이탈 실패', { error: safeErrorMetadata(error) ?? { type: 'Unknown' }, diagnostic_marker: 'menu.waitlist.leave.v1', operation });
    } finally {
      if (pendingPartyActionRef.current === actionScope) pendingPartyActionRef.current = null;
      if (activeActionScopeRef.current === actionScope) setLeaving(false);
    }
  }, [accessToken, store, party, setParty, actionScope]);

  /**
   * 등록 화면으로 복귀 — 종결(착석/취소) 후 재등록 동선.
   *
   * 양쪽 큐가 모두 열린 매장이면 선택 화면(ChooseScreen)으로 되돌린다 — 이전 모드를
   * 그대로 유지하면 재등록 시 다른 큐를 선택할 방법이 없다(#794). 한쪽만 열린
   * 매장은 화면 결정 로직이 그 큐로 자동 이동하므로 mode 를 그대로 둬도 무방하다.
   * storage 의 모드도 함께 지운다 — 남겨두면 다음 방문 때 선택 화면을 건너뛰고 이전
   * 모드로 곧장 복원돼 재선택 동선이 막힌다(#794 리뷰).
   */
  const handleReset = useCallback(() => {
    if (store) {
      clearWaitlistToken(store.id);
      clearWaitlistMode(store.id);
    }
    setAccessToken(null);
    setJoinError(null);
    setArriveError(null);
    // 다음 등록이 이전 번호의 마스킹 값을 잠깐 물려받지 않도록 함께 버린다.
    setContactFallback(null);
    if (
      status?.queues &&
      isWaitlistQueueAvailable(status.queues.table) &&
      isWaitlistQueueAvailable(status.queues.pickup)
    ) {
      setMode(null);
    }
    // 대기 중 매장의 큐 게이트(활성 여부·대기수)가 바뀌었을 수 있으므로 등록 화면
    // 복귀 시 최신 상태를 다시 조회한다(#794 리뷰 — stale 큐 게이트 방지).
    setReloadKey((key) => key + 1);
  }, [store, status]);

  const handleViewMenu = useCallback(() => {
    if (store) {
      navigate(`/restaurant/${store.slug}`);
    }
  }, [navigate, store]);

  /**
   * 픽업 게이트 called 화면 "앱에서 주문하기"(admin#2172/menu#804) — `/restaurant/{slug}`
   * 픽업 모드로 이동한다. RestaurantPage 가 보관 토큰으로 called 상태를 재확인해
   * 주문 CTA 를 노출한다.
   */
  const handleOrderInApp = useCallback(() => {
    if (store) {
      navigate(`/restaurant/${store.slug}`);
    }
  }, [navigate, store]);

  /** 카운트다운 0초 도달 시 15초 폴링을 기다리지 않고 서버 소유 종결 상태를 확인한다. */
  const handleCallExpired = useCallback(() => {
    void refreshParty();
  }, [refreshParty]);

  /** 선택 화면에서 모드를 고르면 storage 에 기억하고 해당 큐의 등록 화면으로 전환한다. */
  const handleChooseMode = useCallback(
    (chosen: WaitlistQueueType) => {
      if (store) {
        saveWaitlistMode(store.id, chosen);
      }
      setMode(chosen);
    },
    [store],
  );

  /**
   * 등록 화면의 "뒤로" — 양쪽 큐가 열린 매장에서만 노출되므로 선택 화면으로 되돌린다.
   * storage 의 모드도 지운다 — 사용자가 명시적으로 재선택 의사를 보인 시점이므로,
   * 선택 없이 새로고침해도 이전 모드로 곧장 복원되지 않아야 한다(#794 리뷰).
   */
  const handleBackToChoose = useCallback(() => {
    if (store) {
      clearWaitlistMode(store.id);
    }
    setMode(null);
  }, [store]);

  const storeName = store?.name ?? '';
  const storeZone = store?.address ?? '';

  const screen = useMemo(() => {
    if (bootstrapping) {
      return (
        <div className="flex flex-1 items-center justify-center px-7 text-sm text-muted-foreground">
          {t.cargando}
        </div>
      );
    }

    if (unavailable) {
      return (
        <WaitlistUnavailableScreen
          t={t}
          storeName={storeName}
          storeZone={storeZone}
          locale={locale}
          onLocaleChange={setLocale}
          reason={unavailable}
          onRetry={() => setReloadKey((key) => key + 1)}
        />
      );
    }

    const storeMeta = status?.store_meta;

    // 토큰 보유자는 접수 중지와 무관하게 자기 대기 상태를 본다 (설계 §8-1).
    // queue_type==='pickup' 은 픽업 전용 체인(대기→호출→준비중→수령준비완료→수령완료)으로
    // 분기하고, 그 외(테이블/구서버 응답)는 기존 체인을 그대로 쓴다(#794 확장).
    if (accessToken && party) {
      if (party.status === 'cancelled') {
        return <WaitlistCancelledScreen t={t} onReset={handleReset} />;
      }

      if (party.queue_type === 'pickup') {
        if (party.status === 'seated') {
          return <WaitlistDeliveredScreen t={t} onReset={handleReset} />;
        }
        if (party.status === 'ready') {
          return <WaitlistPickupReadyScreen t={t} party={party} />;
        }
        if (party.status === 'preparing') {
          return (
            <WaitlistPrepScreen
              t={t}
              storeName={storeName}
              storeZone={storeZone}
              locale={locale}
              onLocaleChange={setLocale}
              storeMeta={storeMeta}
              party={party}
            />
          );
        }
        if (party.status === 'called') {
          return (
            <WaitlistReadyScreen
              t={t}
              party={party}
              gpsEnabled={status?.gps_verification ?? false}
              arriving={arriving}
              leaving={leaving}
              arriveError={arriveError}
              onArrive={handleArrive}
              onLeave={handleLeave}
              onCallExpired={handleCallExpired}
              storeMeta={storeMeta}
              onOrderInApp={handleOrderInApp}
            />
          );
        }
        return (
          <WaitlistWaitingScreen
            t={t}
            storeName={storeName}
            locale={locale}
            onLocaleChange={setLocale}
            party={party}
            startPosition={startPosition}
            leaving={leaving}
            onLeave={handleLeave}
            storeMeta={storeMeta}
            contactFallback={contactFallback}
          />
        );
      }

      // 테이블 (기존 + 구서버 응답 — queue_type 미존재 시 이 분기로 취급).
      if (party.status === 'seated') {
        return (
          <WaitlistSeatedScreen
            t={t}
            party={party}
            onViewMenu={handleViewMenu}
            onReset={handleReset}
            storeMeta={storeMeta}
          />
        );
      }
      if (party.status === 'called') {
        return (
          <WaitlistReadyScreen
            t={t}
            party={party}
            gpsEnabled={status?.gps_verification ?? false}
            arriving={arriving}
            leaving={leaving}
            arriveError={arriveError}
            onArrive={handleArrive}
            onLeave={handleLeave}
            onCallExpired={handleCallExpired}
          />
        );
      }
      return (
        <WaitlistWaitingScreen
          t={t}
          storeName={storeName}
          locale={locale}
          onLocaleChange={setLocale}
          party={party}
          startPosition={startPosition}
          leaving={leaving}
          onLeave={handleLeave}
          storeMeta={storeMeta}
          contactFallback={contactFallback}
        />
      );
    }

    // 토큰이 있으나 첫 상태 응답 전 — 등록 화면이 잠깐 스쳤다 사라지는 깜빡임을 막는다.
    if (accessToken && !party) {
      if (partyError && !partyLoading) {
        return (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-7 text-center" role="alert">
            <p className="text-sm text-muted-foreground">{t.partyReadError}</p>
            <Button onClick={() => { void refreshParty(); }} disabled={partyRefreshing}>
              {t.reintentar}
            </Button>
          </div>
        );
      }
      return (
        <div className="flex flex-1 items-center justify-center px-7 text-sm text-muted-foreground">
          {t.cargando}
        </div>
      );
    }

    // 상태 조회 자체가 아직 없다 — bootstrapping/unavailable 이 모두 아니면 도달하지 않아야
    // 하지만 방어적으로 로딩을 유지한다.
    if (!status) {
      return (
        <div className="flex flex-1 items-center justify-center px-7 text-sm text-muted-foreground">
          {t.cargando}
        </div>
      );
    }

    // 큐 게이트 — 테이블/픽업 2종 큐 확장(#794). `queues` 가 없으면 구서버 응답으로 보고
    // 기존 단일(top-level enabled) 큐 동작으로 폴백한다.
    const { queues } = status;
    const tableAvailable = queues ? isWaitlistQueueAvailable(queues.table) : status.enabled;
    const pickupAvailable = queues ? isWaitlistQueueAvailable(queues.pickup) : false;
    const tableOpen = queues ? isWaitlistQueueOpen(queues.table) : status.enabled;
    const pickupOpen = queues ? isWaitlistQueueOpen(queues.pickup) : false;

    if (!tableAvailable && !pickupAvailable) {
      return (
        <WaitlistUnavailableScreen
          t={t}
          storeName={storeName}
          storeZone={storeZone}
          locale={locale}
          onLocaleChange={setLocale}
          reason="closed"
          storeMeta={storeMeta}
        />
      );
    }

    const bothQueuesAvailable = Boolean(queues && tableAvailable && pickupAvailable);
    const selectedQueueOpen =
      mode === 'table' ? tableOpen : mode === 'pickup' ? pickupOpen : false;

    // 둘 다 운영 중이면 일시정지 큐도 카드로 보여주되 선택은 막는다. 저장된 mode 가
    // 그 사이 paused 로 바뀐 경우에도 선택 화면으로 복귀해 다른 open 큐를 고를 수 있다.
    if (queues && bothQueuesAvailable && (!mode || !selectedQueueOpen)) {
      return (
        <WaitlistChooseScreen
          t={t}
          storeName={storeName}
          storeZone={storeZone}
          locale={locale}
          onLocaleChange={setLocale}
          storeMeta={storeMeta}
          queues={queues}
          onChoose={handleChooseMode}
        />
      );
    }

    // 한쪽만 운영 중이면 그 큐로 자동 진입(mode 선택 무관). 단, 그 큐가 paused 면
    // 등록 폼 대신 사유 화면을 보이며 15초 폴링으로 재개를 감지한다.
    const effectiveMode: WaitlistQueueType =
      bothQueuesAvailable ? (mode as WaitlistQueueType) : tableAvailable ? 'table' : 'pickup';
    const effectiveQueue = queues?.[effectiveMode];

    if (effectiveQueue && !isWaitlistQueueOpen(effectiveQueue)) {
      return (
        <WaitlistUnavailableScreen
          t={t}
          storeName={storeName}
          storeZone={storeZone}
          locale={locale}
          onLocaleChange={setLocale}
          reason="paused"
          pauseReason={effectiveQueue.pause_reason}
          pausedQueue={effectiveMode}
          storeMeta={storeMeta}
        />
      );
    }

    const canGoBack = bothQueuesAvailable;
    // 픽업 큐에는 max_party_size 가 없다(서버가 1로 고정) — 테이블일 때만 큐별 값을 쓴다.
    const maxPartySize =
      effectiveMode === 'table' && queues ? queues.table.max_party_size : status.max_party_size;
    const waitingCount = queues
      ? (effectiveMode === 'pickup' ? queues.pickup : queues.table).waiting_count
      : status.waiting_count;
    const estMinutes = queues
      ? (effectiveMode === 'pickup' ? queues.pickup : queues.table).est_minutes
      : status.est_minutes;

    return (
      <WaitlistFormScreen
        key={`${store?.id ?? 'unknown'}:${reloadKey}:${effectiveMode}`}
        t={t}
        storeName={storeName}
        storeZone={storeZone}
        locale={locale}
        onLocaleChange={setLocale}
        mode={effectiveMode}
        canGoBack={canGoBack}
        onBack={handleBackToChoose}
        maxPartySize={maxPartySize}
        waitingCount={waitingCount}
        estMinutes={estMinutes}
        submitting={joining}
        errorMessage={joinError}
        onJoin={handleJoin}
        storeMeta={storeMeta}
        marketingConsent={
          statusLocale === toApiLocale(locale) ? status.marketing_consent : undefined
        }
        registrationKey={`${store?.id ?? 'unknown'}:${reloadKey}`}
      />
    );
  }, [
    bootstrapping,
    unavailable,
    accessToken,
    party,
    partyLoading,
    partyError,
    partyRefreshing,
    refreshParty,
    status,
    statusLocale,
    mode,
    t,
    storeName,
    storeZone,
    locale,
    reloadKey,
    store?.id,
    setLocale,
    startPosition,
    contactFallback,
    joining,
    joinError,
    arriving,
    arriveError,
    leaving,
    handleJoin,
    handleArrive,
    handleLeave,
    handleReset,
    handleViewMenu,
    handleOrderInApp,
    handleCallExpired,
    handleChooseMode,
    handleBackToChoose,
  ]);

  return (
    <div
      data-testid="waitlist-page"
      dir={direction}
      className="flex min-h-screen flex-col bg-background text-foreground supports-[height:100dvh]:min-h-dvh"
    >
      {partyScreenActive && partyStale && (
        <div role="status" className="mx-5 my-4 rounded-md bg-background-secondary p-4 text-sm">
          <p className="mb-3 text-muted-foreground">{t.partyReadStale}</p>
          <Button variant="outline" onClick={() => { void refreshParty(); }} disabled={partyRefreshing}>
            {t.reintentar}
          </Button>
        </div>
      )}
      {screen}
      {storeFollowVisible && (
        <WaitlistStoreFollowToggle
          t={t}
          followed={storeFollow.followed}
          pending={storeFollow.pending}
          failed={storeFollow.failed}
          reconsentRequired={storeFollow.reconsentRequired}
          onToggle={() => {
            void storeFollow.toggle();
          }}
        />
      )}
      {/*
        `current === false` 일 때는 토글도 철회 링크도 뜨지 않고 **아무 말도 하지 않는다**.
        그 값은 실제 OFF 뿐 아니라 판정 불가(Platform Care 킬스위치 OFF·공지 계약 결손·
        연락처 신원 모호에서의 fail-closed)까지 덮으므로, "이 번호는 프로모션 수신이 꺼져
        있어요" 같은 단정은 공지 교체 직후나 킬스위치 중 전 고객에게 거짓이 된다(#885
        리뷰). 서버가 두 상태를 구분해 내려주기 전까지는 침묵이 정직하다.
      */}
      {consentWithdrawal.visible && (
        <WaitlistMarketingConsentWithdraw
          t={t}
          withdrawing={consentWithdrawal.withdrawing}
          withdrawn={consentWithdrawal.withdrawn}
          failed={consentWithdrawal.failed}
          unavailable={consentWithdrawal.unavailable}
          onWithdraw={() => {
            void consentWithdrawal.withdraw();
          }}
        />
      )}
    </div>
  );
}
